/**
 * ProcessingModal
 * Shows processing status and allows user to close and navigate to request
 * Polls job status and updates UI accordingly
 */

import { useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle,
  XCircle,
  Loader,
  ExternalLink,
} from 'lucide-react-native';
import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  ActivityIndicator,
  Animated,
  Platform,
} from 'react-native';

import { useJobStatus } from '../../hooks/queries/useJobStatus';
import { offlineRequestsKeys } from '../../hooks/queries/useOfflineRequestsQuery';
import { requestsKeys } from '../../hooks/queries/useRequestsQuery';
import { colors } from '../../theme/colors';
import { TranscriptionJobStatus } from '../../types/transcription';

export interface ProcessingModalProps {
  visible: boolean;
  onClose: () => void;
  onNavigateToRequest?: (requestId: number) => void;
  requestId?: number;
  fileName?: string;
  /** Hide the "View Request" button (use when already on request detail page) */
  hideViewRequestButton?: boolean;
  /**
   * When false, the modal stays open on COMPLETED/FAILED until the user dismisses
   * it (no 30s auto-close), so the outcome can't be missed. Defaults to true to
   * preserve the existing audio/document import behavior. Text intake passes false.
   */
  autoCloseOnComplete?: boolean;
}

export const ProcessingModal: React.FC<ProcessingModalProps> = ({
  visible,
  onClose,
  onNavigateToRequest,
  requestId,
  fileName,
  hideViewRequestButton = false,
  autoCloseOnComplete = true,
}) => {
  const queryClient = useQueryClient();
  const spinAnim = useRef(new Animated.Value(0)).current;
  const hasInvalidatedRef = useRef(false);
  const animationRef = useRef<Animated.CompositeAnimation | null>(null);
  const [processingDuration, setProcessingDuration] = useState<string | null>(
    null,
  );
  const autoCloseTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Hold the latest onClose in a ref so the auto-close effect doesn't depend
  // on an unstable inline callback from the parent. Parents typically pass
  // `() => setVisible(false)` inline, which produces a new reference on every
  // re-render (e.g., from background polling) and previously caused the 30s
  // timer to be cleared and re-armed forever, so it never fired.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Poll job status every 5 seconds
  const { data: jobStatus, refetch } = useJobStatus(requestId, {
    enabled: visible && !!requestId,
    refetchInterval: 5000, // Poll every 5 seconds
  });

  // Calculate and update processing duration based on job timestamps
  useEffect(() => {
    if (!jobStatus) {
      setProcessingDuration(null);
      return;
    }

    // Only show duration when completed or failed
    if (
      jobStatus.status !== TranscriptionJobStatus.COMPLETED &&
      jobStatus.status !== TranscriptionJobStatus.FAILED
    ) {
      setProcessingDuration(null);
      return;
    }

    const startTime = new Date(jobStatus.createdAt).getTime();
    let endTime: number;

    // Use completedAt if available, otherwise fallback to current time
    if (jobStatus.completedAt) {
      endTime = new Date(jobStatus.completedAt).getTime();
    } else {
      // Fallback: use current time if completedAt is not available
      endTime = Date.now();
    }

    const durationMs = endTime - startTime;
    const durationSeconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(durationSeconds / 60);
    const seconds = durationSeconds % 60;

    let durationText = '';
    if (minutes > 0) {
      durationText = `${minutes} minute${minutes !== 1 ? 's' : ''}`;
      if (seconds > 0) {
        durationText += ` ${seconds} second${seconds !== 1 ? 's' : ''}`;
      }
    } else {
      durationText = `${seconds} second${seconds !== 1 ? 's' : ''}`;
    }

    setProcessingDuration(durationText);
  }, [jobStatus?.status, jobStatus?.completedAt, jobStatus?.createdAt]);

  // Spin animation for processing state - continuous loop every 2 seconds
  // Start spinning immediately when modal is visible and keep spinning until completed/failed
  useEffect(() => {
    if (!visible || !requestId) {
      // Stop animation when modal is not visible
      if (animationRef.current) {
        animationRef.current.stop();
        animationRef.current = null;
      }
      spinAnim.setValue(0);
      return;
    }

    const isProcessing =
      !jobStatus || // Start spinning immediately if jobStatus hasn't loaded yet
      jobStatus?.status === TranscriptionJobStatus.PROCESSING ||
      jobStatus?.status === TranscriptionJobStatus.PENDING;

    const isCompleted = jobStatus?.status === TranscriptionJobStatus.COMPLETED;
    const isFailed = jobStatus?.status === TranscriptionJobStatus.FAILED;

    // Only start animation if it's not already running and we should be processing
    if (isProcessing && !isCompleted && !isFailed) {
      // Only start if animation is not already running
      if (!animationRef.current) {
        // Start continuous spinning animation (2 seconds per rotation)
        // Use a fresh Animated.Value to avoid conflicts
        const startValue = 0;
        spinAnim.setValue(startValue);

        animationRef.current = Animated.loop(
          Animated.timing(spinAnim, {
            toValue: 1,
            duration: 2000, // 2 seconds per rotation
            useNativeDriver: true,
          }),
        );
        animationRef.current.start();
      }
      // If animation is already running, don't do anything - let it continue uninterrupted
    } else if (isCompleted || isFailed) {
      // Stop animation only when actually completed or failed
      if (animationRef.current) {
        animationRef.current.stop();
        animationRef.current = null;
      }
      spinAnim.setValue(0);
    }

    return () => {
      // Cleanup on unmount - but don't stop if we're still processing
      // The cleanup in the visibility check above handles stopping when needed
    };
  }, [jobStatus?.status, visible, requestId]);

  // Invalidate queries when processing completes or fails
  useEffect(() => {
    if (
      requestId &&
      (jobStatus?.status === TranscriptionJobStatus.COMPLETED ||
        jobStatus?.status === TranscriptionJobStatus.FAILED) &&
      !hasInvalidatedRef.current
    ) {
      hasInvalidatedRef.current = true;

      // Invalidate request list queries
      queryClient.invalidateQueries({ queryKey: offlineRequestsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: requestsKeys.all });

      // Invalidate specific request detail query
      queryClient.invalidateQueries({
        queryKey: offlineRequestsKeys.detail(String(requestId)),
      });
      queryClient.invalidateQueries({
        queryKey: requestsKeys.detail(String(requestId)),
      });
    }
  }, [jobStatus?.status, requestId, queryClient]);

  // Auto-close dialog after 30 seconds when processing completes or fails
  useEffect(() => {
    // Clear any existing timer
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }

    // Only set timer if auto-close is enabled and the job is completed or failed.
    // When autoCloseOnComplete is false (text intake), the modal stays open until
    // the user dismisses it so the outcome is never missed.
    if (
      autoCloseOnComplete &&
      visible &&
      jobStatus &&
      (jobStatus.status === TranscriptionJobStatus.COMPLETED ||
        jobStatus.status === TranscriptionJobStatus.FAILED)
    ) {
      // Set timer to close after 30 seconds
      autoCloseTimerRef.current = setTimeout(() => {
        onCloseRef.current();
      }, 30000); // 30 seconds
    }

    // Cleanup timer on unmount or when conditions change
    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, [visible, jobStatus?.status, autoCloseOnComplete]);

  // Reset invalidation flag and stop animation when modal closes or requestId changes
  useEffect(() => {
    if (!visible || !requestId) {
      hasInvalidatedRef.current = false;
      // Stop animation when modal closes
      if (animationRef.current) {
        animationRef.current.stop();
        animationRef.current = null;
      }
      spinAnim.setValue(0);
      // Clear auto-close timer when modal closes
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    }
  }, [visible, requestId]);

  const handleNavigateToRequest = () => {
    if (requestId && onNavigateToRequest) {
      onNavigateToRequest(requestId);
    }
    onClose();
  };

  const renderStatusContent = () => {
    // Show spinning loader when jobStatus is null (initial loading) or when processing
    if (
      !jobStatus ||
      jobStatus.status === TranscriptionJobStatus.PENDING ||
      jobStatus.status === TranscriptionJobStatus.PROCESSING
    ) {
      const spin = spinAnim.interpolate({
        inputRange: [0, 1],
        outputRange: ['0deg', '360deg'],
      });
      return (
        <View style={styles.statusContainer}>
          <Animated.View
            style={[styles.iconContainer, { transform: [{ rotate: spin }] }]}
          >
            <Loader size={48} color={colors.primary} />
          </Animated.View>
          <Text testID="processing-status-title" style={styles.statusTitle}>
            {jobStatus?.fileType === 'text'
              ? 'Processing Your Text'
              : 'Processing Your File'}
          </Text>
          <Text style={styles.statusDescription}>
            {jobStatus?.fileType === 'text' ? (
              'AI is extracting the request fields from your pasted text.'
            ) : (
              <>
                AI is extracting information from{' '}
                <Text style={styles.fileName}>
                  {fileName || jobStatus?.fileName || 'your file'}
                </Text>
              </>
            )}
          </Text>
          <Text style={styles.statusHint}>
            This typically takes 2-5 minutes. You can close this window and
            check back later.
          </Text>
        </View>
      );
    }

    switch (jobStatus.status) {
      case TranscriptionJobStatus.COMPLETED:
        return (
          <View style={styles.statusContainer}>
            <View style={[styles.iconContainer, styles.successIconContainer]}>
              <CheckCircle size={48} color={colors.success} />
            </View>
            <Text
              testID="processing-status-title"
              style={[styles.statusTitle, styles.successTitle]}
            >
              Processing Complete!
            </Text>
            <Text style={styles.statusDescription}>
              Your request has been updated with extracted information.
            </Text>
            {processingDuration && (
              <Text style={styles.durationText}>
                Processing completed in {processingDuration}
              </Text>
            )}
            {!hideViewRequestButton && (
              <TouchableOpacity
                testID="processing-view-request-btn"
                style={styles.primaryButton}
                onPress={handleNavigateToRequest}
              >
                <Text style={styles.primaryButtonText}>View Request</Text>
                <ExternalLink
                  size={16}
                  color="#FFFFFF"
                  style={styles.buttonIcon}
                />
              </TouchableOpacity>
            )}
          </View>
        );

      case TranscriptionJobStatus.FAILED:
        return (
          <View style={styles.statusContainer}>
            <View style={[styles.iconContainer, styles.errorIconContainer]}>
              <XCircle size={48} color="#E74C3C" />
            </View>
            <Text
              testID="processing-status-title"
              style={[styles.statusTitle, styles.errorTitle]}
            >
              Processing Failed
            </Text>
            <Text style={styles.statusDescription}>
              {jobStatus.errorMessage ||
                'Unable to process the file. No fields were extracted.'}
            </Text>
            {jobStatus.errorCode && (
              <Text style={styles.errorCode}>
                Error Code: {jobStatus.errorCode}
              </Text>
            )}
            {!hideViewRequestButton && (
              <TouchableOpacity
                testID="processing-view-request-btn"
                style={styles.primaryButton}
                onPress={handleNavigateToRequest}
              >
                <Text style={styles.primaryButtonText}>View Request</Text>
                <ExternalLink
                  size={16}
                  color="#FFFFFF"
                  style={styles.buttonIcon}
                />
              </TouchableOpacity>
            )}
          </View>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View testID="processing-modal" style={styles.modalContainer}>
          {/* Content */}
          <View style={styles.content}>{renderStatusContent()}</View>

          {/* Footer */}
          <View style={styles.footer}>
            <TouchableOpacity
              testID="processing-close-btn"
              style={styles.secondaryButton}
              onPress={onClose}
            >
              <Text style={styles.secondaryButtonText}>
                {jobStatus?.status === TranscriptionJobStatus.COMPLETED ||
                jobStatus?.status === TranscriptionJobStatus.FAILED
                  ? 'Close'
                  : 'Close (processing continues)'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    width: '100%',
    maxWidth: 500,
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
  },
  content: {
    padding: 32,
  },
  statusContainer: {
    alignItems: 'center',
  },
  iconContainer: {
    marginBottom: 20,
  },
  successIconContainer: {
    backgroundColor: '#E8F5E9',
    borderRadius: 50,
    padding: 16,
  },
  errorIconContainer: {
    backgroundColor: '#FFEBEE',
    borderRadius: 50,
    padding: 16,
  },
  statusTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.primary,
    marginBottom: 12,
    textAlign: 'center',
  },
  successTitle: {
    color: colors.success,
  },
  errorTitle: {
    color: '#E74C3C',
  },
  statusDescription: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 8,
  },
  fileName: {
    fontWeight: '600',
    color: colors.primary,
  },
  statusHint: {
    fontSize: 12,
    color: '#999',
    textAlign: 'center',
    lineHeight: 18,
    marginTop: 8,
    fontStyle: 'italic',
  },
  errorCode: {
    fontSize: 12,
    color: '#E74C3C',
    marginTop: 8,
    fontFamily: 'monospace',
  },
  durationText: {
    fontSize: 13,
    color: '#666',
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 4,
    fontStyle: 'italic',
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FF6B35',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    marginTop: 20,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
        transition: 'background-color 0.2s' as any,
      },
    }),
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    marginRight: 8,
  },
  buttonIcon: {
    marginTop: 2,
  },
  footer: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
  },
  secondaryButton: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F0F0F0',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
        transition: 'background-color 0.2s' as any,
      },
    }),
  },
  secondaryButtonText: {
    color: '#666',
    fontSize: 14,
    fontWeight: '600',
  },
});
